<script>
   /*************************************************************/
   /* DistancePlot for PCA results                              */
   /* ----------------------------                              */
   /* shows plot with score and orthogonal distances            */
   /* and acceptance areas  (both for objects and variables)    */
   /*************************************************************/

   import { max } from 'mdatools/stat';
   import { Axes, XAxis, YAxis, Box, Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import { Vector } from 'mdatools/arrays';
   import { qchisq } from 'mdatools/distributions';

   import { getColorGroups, PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '../../../shared/plots/Defaults.js';
   import { pcares_get } from '../../../shared/wasm/dist/pca.js';
   import PlotSeries from '../../../shared/plots/PlotSeries.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   export let params;                     // JSON with main plot parameters
   export let res;                        // JSON with PCA results
   export let selected = undefined;       // selected object or variable

   export let fileName = 'plot-distance';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;

   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const showGrid = true;
   const plotType = 'p';
   const LINE_COLOR = '#909090';    // color of the limits lines

   // const parameters for computation
   const NPOINTS = 100;             // number of points in limit lines

   // local copies of user defined plot parameters
   let limType = 'classic';
   let transType = 'no';
   let showLabels = 'no';
   let colorBy = 'none';
   let ncomp = 1;

   // function for creating axis titles
   function get_axis_label(id, transType) {
      const distNames = {
         'q': 'Orthogonal distance',
         'h': 'Score distance',
      };
      return distNames[id] + ', ' + (transType === 'log' ? 'log(1 + ' + id + '/' + id + '0)' : id + '/' + id + '0');
   };

   function get_limit_coordinates(prob, Nh, Nq, transType) {
      const Nf = Nh + Nq;
      const crit = qchisq(prob, Nf);

      // show limit for orthogonal and score distance
      const b0 = crit/Nq;
      const b1 = -Nh / Nq;
      const xMax = -b0 / b1;
      const x = Vector.seq(0, xMax * 1, xMax  / NPOINTS);
      const y = x.apply(v => v * b1 + b0);
      return {
         x:  transType === 'log' ? x.apply(v => Math.log(1 + v)) : x,
         y:  transType === 'log' ? y.apply(v => Math.log(1 + v)) : y
      }
   }

   function select(e) {
      if (selected === undefined) return; // do not select points if this option is not activated in parent app
      const id = parseInt(e.detail.elementID)
      const series_title = e.detail.seriesTitle
      if (id >= 0 && series_title === 'distance-points') {
         selected = id;
      }
   }

   function resetSelection(e) {
      selected = -1;
   }


   // reactive expressions to monitor changes in plot parameters
   $: if (params && params.limType !== limType) limType = params.limType;
   $: if (params && params.transType !== transType) transType = params.transType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.ncomp) ncomp = params.ncomp;
   $: if (params && params.colorBy) colorBy = params.colorBy;

   // reactive expression to compute x and y coordinates of limits
   let els, ols;
   $: if (res && ncomp > 0) {
      const l = limType === 'classic' ? 'c' : 'r';
      const Nh = res[l + 'Nh'];
      const Nq = res[l + 'Nq']
      els = get_limit_coordinates(0.95, Nh[ncomp - 1], Nq[ncomp - 1], transType);
      ols = get_limit_coordinates(Math.pow(0.99, 1/res.nrows), Nh[ncomp - 1], Nq[ncomp - 1], transType);
      yMax = ols.y.v[0];
   }

   // reactive expression to compute colorby parameters
   let collabels, colmap, colbreaks, objcolors, colorVarName;
   $: [collabels, colmap, colbreaks, objcolors, colorVarName] = getColorGroups(null, res, colorBy, null);

   // reactive expression to compute coordinates of points and colors
   let xValues, yValues, xMax, yMax, xMin, yMin;
   $: if (res && ncomp > 0) {

      // select distances to show and their scales
      const res_xd = pcares_get(res, "hdist", ncomp);
      const res_yd = pcares_get(res, "qdist", ncomp);

      const l = limType === 'classic' ? 'c' : 'r';
      const res_xd0 = res[l + 'h0'][ncomp - 1];
      const res_yd0 = res[l + 'q0'][ncomp - 1];

      // prepare vectors for transformed distances
      const n = res_xd.length;
      xValues = Vector.zeros(n);
      yValues = Vector.zeros(n);

      yMax = Number.NEGATIVE_INFINITY;
      yMin = Number.POSITIVE_INFINITY;
      xMax = Number.NEGATIVE_INFINITY;
      xMin = Number.POSITIVE_INFINITY;


      if (transType === 'log') {
         for (let i = 0; i < n; i++) {
            const xv = Math.log(1 + res_xd[i]/res_xd0);
            const yv = Math.log(1 + res_yd[i]/res_yd0);
            xValues.v[i] = xv;
            yValues.v[i] = yv
            xMax = Math.max(xMax, xv);
            yMax = Math.max(yMax, yv);
            xMin = Math.min(xMin, xv);
            yMin = Math.min(yMin, yv);
         }
      } else {
         for (let i = 0; i < n; i++) {
            const xv = res_xd[i]/res_xd0;
            const yv = res_yd[i]/res_yd0;
            xValues.v[i] = xv;
            yValues.v[i] = yv
            xMax = Math.max(xMax, xv);
            yMax = Math.max(yMax, yv);
            xMin = Math.min(xMin, xv);
            yMin = Math.min(yMin, yv);
         }
      }
   } else {
      xValues = null;
      yValues = null;
      yMax = 1;
      yMin = 0;
      xMax = 1;
      xMin = 0;
   }

   // reactive calculations for axis labels and title
   $: title = 'Distance plot (A = ' + ncomp + ')';
   $: xLabel = get_axis_label('h', transType);
   $: yLabel = get_axis_label('q', transType);

   // reactive calculations for axis limits
   $: xMaxTot = max([xMax, ols.x.v[NPOINTS - 1]]);
   $: yMaxTot = max([yMax, ols.y.v[0]]);
   $: limX = [-0.02 * (xMaxTot - xMin), 1.05 * xMaxTot];
   $: limY = [-0.02 * (yMaxTot - yMin), (1.05 + 0.10 * (colorBy !== 'none')) * yMaxTot];
</script>

{#if res && params}
<div class="plot-container">
<Axes
   margins={MARGINS}
   on:markerclick={select}
   on:axesclick={resetSelection}
   downloadLinks="hover"
   {fileName}
   {xLabel}
   {yLabel}
   {limX}
   {limY}
   {title}
   {pngWidth}
   {pngHeight}
   {pngRes}
>
   <!-- limits  -->
   {#if els }
   <Lines xValues={els.x} yValues={els.y} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if ols}
   <Lines xValues={ols.x} yValues={ols.y} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {plotType}
      {showLabels}
      {selected}
      labels={res.objlabels}
      colors={objcolors}
      seriesTitle="distance"
      bind:minYVal={yMin}
      bind:maxYVal={yMax}
      bind:minXVal={xMin}
      bind:maxXVal={xMax}
   />


   <!-- color by legend-->
   {#if colorBy === 'groups' && colmap }
   <ColormapLegend {colmap} breaks={colbreaks} labels={collabels}/>
   {/if}

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
</div>
{#if selected !== undefined}
   {#if selected < 0}
   <div class="msg-select">Click on a point to select...</div>
   {:else}
   <div class="msg-select">Click anywhere to remove selection...</div>
   {/if}
{/if}
{/if}


<style>
   .plot-container {
      width: 100%;
      height: calc(100% - 15px);
   }
   .msg-select {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>

